/** TanStack Query hooks for the admin candidate-review surface (Plan 03-07). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as api from "./api";
import type { StatusFilter } from "./api";

export function useCandidatesQuery(positionId: number, status: StatusFilter) {
  return useQuery({
    queryKey: ["admin-candidates", positionId, status],
    queryFn: () => api.listCandidatesForPosition(positionId, status),
    enabled: Number.isFinite(positionId),
  });
}

export function useCandidateDetailQuery(id: number) {
  return useQuery({
    queryKey: ["admin-candidate", id],
    queryFn: () => api.getCandidateDetail(id),
    enabled: Number.isFinite(id),
    retry: false, // 404 → surface immediately for T-1 redirect
  });
}

/** Invalidate every candidate list + this candidate's detail after a state mutation. */
function useCandidateInvalidation(id: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["admin-candidates"] });
    void qc.invalidateQueries({ queryKey: ["admin-candidate", id] });
    // BUG-088 (2026-06-04): any candidate state mutation (promote/demote/rubric-save/
    // shortlist-tag) can change the ranked Lista corta — refresh it so the ShortlistView
    // row updates (Promover↔Demote) and re-ranks instead of going stale. Without this the
    // promote looked like a no-op, the user re-clicked, and hit candidate_not_stage2_done.
    void qc.invalidateQueries({ queryKey: ["position-shortlist"] });
  };
}

export function useAdvanceMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: (note?: string) => api.advanceCandidate(id, note),
    onSuccess: invalidate,
  });
}

export function useRejectMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: (reason?: string) => api.rejectCandidate(id, reason),
    onSuccess: invalidate,
  });
}

export function useUnrejectMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: () => api.unrejectCandidate(id),
    onSuccess: invalidate,
  });
}

/**
 * Stage 2 invite copy — manual trigger (enabled:false). The button calls
 * .refetch() on click and reads data.body_text for the clipboard.
 */
export function useStage2InviteCopyQuery(
  id: number,
  language: "auto" | "es" | "en" | "de",
) {
  return useQuery({
    queryKey: ["stage2-invite-copy", id, language],
    queryFn: () => api.getStage2InviteCopy(id, language),
    enabled: false,
    gcTime: 0,
  });
}

// ---- Plan 04-07: Stage 2 decision-support hooks ----

/** Mint a fresh 5-min vtoken for inline playback (FIN-01). Re-fetch on play error / remount. */
export function useVideoTokenQuery(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ["candidate-video-token", id],
    queryFn: () => api.mintVideoToken(id),
    enabled: enabled && Number.isFinite(id),
    staleTime: 4 * 60 * 1000, // 5-min token; refetch a little before expiry
    retry: false,
  });
}

/** Save the rubric → composite recompute; invalidate detail so the prefill + stage refresh. */
export function useSaveRubricMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: (payload: api.RubricSavePayload) => api.saveRubric(id, payload),
    onSuccess: invalidate,
  });
}

export function useShortlistTagMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: (tag: api.ShortlistTag) => api.setShortlistTag(id, tag),
    onSuccess: invalidate,
  });
}

export function usePromoteFinalistMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: () => api.promoteFinalist(id),
    onSuccess: invalidate,
  });
}

export function useDemoteFinalistMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: () => api.demoteFinalist(id),
    onSuccess: invalidate,
  });
}

/** OPS-04: finalist → hired (terminal). */
export function useSetHiredMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: () => api.setHired(id),
    onSuccess: invalidate,
  });
}

/** OPS-04: update the HR-editable hiring_status_notes on a finalist. */
export function useUpdateHiringStatusNotesMutation(id: number) {
  const invalidate = useCandidateInvalidation(id);
  return useMutation({
    mutationFn: (notes: string) => api.updateHiringStatusNotes(id, notes),
    onSuccess: invalidate,
  });
}

export function useShortlistQuery(positionId: number) {
  return useQuery({
    queryKey: ["position-shortlist", positionId],
    queryFn: () => api.getShortlist(positionId),
    enabled: Number.isFinite(positionId),
  });
}

export function usePositionSummaryQuery(positionId: number, enabled = true) {
  return useQuery({
    queryKey: ["position-summary", positionId],
    queryFn: () => api.getPositionSummary(positionId),
    enabled: enabled && Number.isFinite(positionId),
    // Matches the dashboard list staleTime so client-side filter remounts serve from
    // cache — filtering never triggers a refetch (preserves the D-15 invariant).
    staleTime: 30_000,
  });
}
